// packages import
import { Box, TableBody, TableCell, TableHead, TableRow, useMediaQuery } from '@mui/material'
import { FC, useContext } from 'react'
import { useSearchParams } from 'react-router-dom'
// component import
import { ContentHead } from '../../common/ContentHead'
import { CustomPagination } from '../../common/CustomPagination'
import { EmptyData } from '../../common/EmptyData'
import { UploadCsv } from '../../common/UploadCsv'
import { UploadInitialCsv } from '../../common/UploadInitialCsv'
import { CasesSkeleton } from '../../skeletons/CasesSkeleton'
import { FilterWrapper } from '../FilterWrapper'
import { ArchiveCase } from '../myCases/ArchiveCase'
import { CompleteCase } from '../myCases/CompleteCase'
import { DeleteCase } from '../myCases/DeleteCase'
import { DisplayCaseItem } from '../myCases/DisplayCaseItem'
// others
import { CASES_TABLE_HEADERS, DCPP, NO_CASES_FOUND, NO_CONSULTS_FOUND, QUERY_PARAMS } from '../../../constants'
import { AppContext, AuthContext, CaseContext } from '../../../context'
import { ActionType } from '../../../context/AppContextReducer'
import { CaseProps } from '../../../interfaceTypes'
import { CardBox, CustomTabButton, FilterOuterWrapper } from '../../../theme/styleComponents'
import { flexCenter } from '../../../theme/styleConstants'
import { CaseStickyTable } from '../../common/CaseStickyTable'
import { CaseActionType } from '../caseReducer'

export const AllCases: FC<CaseProps & { hideTabs?: boolean }> = ({ loading, refetch, isConsultPath, buttonLink = 'create', hideTabs = false, fetchAllCases }) => {
  const { dispatch: reducerDispatch } = useContext(AppContext)
  const { isAdmin, isAttorney, isInvestigator, isParalegal, isAccountant, isDcpp } = useContext(AuthContext)
  const { dispatch, caseFilter, allCases, isWorkCompleted } = useContext(CaseContext)
  const casesLength = allCases?.length
  const [searchParams, setSearchParams] = useSearchParams()
  const mediumViewport = useMediaQuery('(max-width:1138px)')
  const filterAllCases = allCases?.filter(litigation => {
    const { caseType } = litigation || {}
    const { name } = caseType || {}

    return name !== DCPP
  })

  const cases = (isAdmin || isAccountant || isDcpp) ? allCases : filterAllCases
  const isEmpty = cases?.length === 0 && !loading
  const isParalegalOrAttorney = isParalegal || isAttorney

  return (
    <CardBox marginTop={3}>
      {!isAccountant && (
        <ContentHead
          title={`All ${isConsultPath ? 'Consults' : 'Matters'}`}
          buttonText={isInvestigator ? '' : `Add ${isConsultPath ? 'Consult' : 'a New Matter'}`}
          initialCsv={isAdmin}
          link={buttonLink}
          csvButton={isAdmin ? 'Upload Csv' : ''}
          isCase
        />
      )}

      {!hideTabs && (
        <Box padding="20px 0px 0px 15px">
          <CustomTabButton
            isLeftItem
            onClick={() => {
              dispatch({ type: CaseActionType.SET_SEARCH_VALUE, searchValue: '' })
              dispatch({ type: CaseActionType.SET_CASE_FILTER, caseFilter: 'active' })
              dispatch({ type: CaseActionType.SET_LAW_TYPE_ID, lawTypeId: '' })
              reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
              dispatch({ type: CaseActionType.SET_IS_WORK_COMPLETED, isWorkCompleted: false })
              searchParams.set('caseFilter', 'active')
              searchParams.set('currentPage', '1')
              searchParams.set('searchValue', '')
              searchParams.set('lawType', '')
              searchParams.set(QUERY_PARAMS.IS_WORK_COMPLETED, 'no')
              setSearchParams(searchParams)
            }}
            className={caseFilter === 'active' && !isWorkCompleted ? 'MuiTab-Custom Mui-selected' : 'MuiTab-Custom'}
          >
            Active
          </CustomTabButton>

          <CustomTabButton
            isCenterItem
            onClick={() => {
              dispatch({ type: CaseActionType.SET_SEARCH_VALUE, searchValue: '' })
              dispatch({ type: CaseActionType.SET_CASE_FILTER, caseFilter: 'active' })
              dispatch({ type: CaseActionType.SET_LAW_TYPE_ID, lawTypeId: '' })
              dispatch({ type: CaseActionType.SET_IS_WORK_COMPLETED, isWorkCompleted: true })
              reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
              searchParams.set('caseFilter', 'active')
              searchParams.set('currentPage', '1')
              searchParams.set('searchValue', '')
              searchParams.set('lawType', '')
              searchParams.set(QUERY_PARAMS.IS_WORK_COMPLETED, 'yes')
              setSearchParams(searchParams)
            }}
            className={isWorkCompleted ? 'MuiTab-Custom Mui-selected' : 'MuiTab-Custom'}
          >
            Work Completed
          </CustomTabButton>

          <CustomTabButton
            onClick={() => {
              dispatch({ type: CaseActionType.SET_CASE_FILTER, caseFilter: 'close' })
              reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
              dispatch({ type: CaseActionType.SET_SEARCH_VALUE, searchValue: '' })
              dispatch({ type: CaseActionType.SET_LAW_TYPE_ID, lawTypeId: '' })
              dispatch({ type: CaseActionType.SET_IS_WORK_COMPLETED, isWorkCompleted: undefined })
              searchParams.set('caseFilter', 'close')
              searchParams.set('currentPage', '1')
              searchParams.set('searchValue', '')
              searchParams.set('lawType', '')
              searchParams.delete(QUERY_PARAMS.IS_WORK_COMPLETED)
              setSearchParams(searchParams)
            }}
            className={caseFilter === 'close' ? 'MuiTab-Custom Mui-selected' : 'MuiTab-Custom'}
          >
            Closed
          </CustomTabButton>
        </Box>
      )}

      <FilterOuterWrapper justifyContent={mediumViewport ? 'flex-end' : 'space-between'}>
        <FilterWrapper casesLength={casesLength!} sortByUser showClient={false} loading={loading} refetch={refetch} fetchAllCases={fetchAllCases} />
      </FilterOuterWrapper>

      {isEmpty ? (
        <Box sx={{ ...flexCenter, height: 'calc(100vh - 417px)' }}>
          <EmptyData title={isConsultPath ? NO_CONSULTS_FOUND : NO_CASES_FOUND} />
        </Box>
      ) : (
        <CaseStickyTable
          pagination={<CustomPagination loading={loading} setParams />}
          loading={loading}
          height={isParalegalOrAttorney ? (mediumViewport ? '435px' : '498px') : mediumViewport ? '361px' : '424px'}
        >
          <TableHead>
            <TableRow>
              {CASES_TABLE_HEADERS.map((item, index) => {
                const lastIndex = CASES_TABLE_HEADERS.length - 1

                return (
                  <TableCell
                    component="th"
                    key={`${item}-${index}`}
                    sx={{ minWidth: index === lastIndex ? '50px' : '150px ' }}
                  >
                    {item}
                  </TableCell>
                )
              })}
            </TableRow>
          </TableHead>

          {loading ? (
            <CasesSkeleton noOfRows={6} />
          ) : (
            <TableBody>
              <DisplayCaseItem />
            </TableBody>
          )}
        </CaseStickyTable>
      )}

      <CompleteCase refetch={refetch} />

      <ArchiveCase refetch={refetch} />

      <DeleteCase refetch={refetch} />

      <UploadInitialCsv refetch={refetch} />

      <UploadCsv isCase refetchCases={refetch} />
    </CardBox>
  )
}
